/**
 * Free Lease Expiry Tracker Tool
 * Estate Flow - Sebastian Alliance Group LLC
 * 
 * Public lead magnet tool - no login required
 * Captures email for marketing follow-up
 * Co-brandable with client logo
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Calendar, Plus, Trash2, Download, Upload, Bell, Home, Building2,
  ArrowRight, Mail, Image, X, AlertTriangle, CheckCircle, Clock,
  User, DollarSign, FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FreeLeaseTracker = () => {
  const [email, setEmail] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [clientLogo, setClientLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [leases, setLeases] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [emailReminders, setEmailReminders] = useState(true);
  const [newLease, setNewLease] = useState({
    property: '',
    unit: '',
    tenant_name: '',
    tenant_email: '',
    tenant_phone: '',
    lease_start: '',
    lease_end: '',
    monthly_rent: '',
    notes: ''
  });

  // Load saved data from localStorage
  useEffect(() => {
    const savedEmail = localStorage.getItem('freetools_email');
    const savedLeases = localStorage.getItem('lease_tracker_items');
    const savedLogo = localStorage.getItem('lease_tracker_logo');
    const savedCompany = localStorage.getItem('lease_tracker_company');
    
    if (savedEmail) {
      setEmail(savedEmail);
      setIsUnlocked(true);
    }
    if (savedLeases) {
      setLeases(JSON.parse(savedLeases));
    }
    if (savedLogo) {
      setClientLogo(savedLogo);
    }
    if (savedCompany) {
      setCompanyName(savedCompany);
    }
  }, []);

  // Save leases to localStorage whenever they change
  useEffect(() => {
    if (leases.length > 0) {
      localStorage.setItem('lease_tracker_items', JSON.stringify(leases));
    }
  }, [leases]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      // Save email to backend for marketing
      await fetch(`${BACKEND_URL}/api/free-tools/capture-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          tool: 'lease_tracker',
          source: 'free_tools_page'
        })
      });
    } catch (error) {
      console.log('Lead capture failed, continuing anyway');
    }

    localStorage.setItem('freetools_email', email);
    setIsUnlocked(true);
    toast.success('Welcome! Your free lease tracker is ready.');
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setClientLogo(reader.result);
        localStorage.setItem('lease_tracker_logo', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCompanyNameChange = (e) => {
    setCompanyName(e.target.value);
    localStorage.setItem('lease_tracker_company', e.target.value);
  };

  const addLease = () => {
    if (!newLease.property || !newLease.lease_end || !newLease.tenant_name) {
      toast.error('Please fill in property, tenant name, and lease end date');
      return;
    }

    const lease = {
      ...newLease,
      id: Date.now(),
      created_at: new Date().toISOString()
    };

    setLeases([lease, ...leases]);
    setNewLease({
      property: '',
      unit: '',
      tenant_name: '',
      tenant_email: '',
      tenant_phone: '',
      lease_start: '',
      lease_end: '',
      monthly_rent: '',
      notes: ''
    });
    setShowAddForm(false);
    toast.success('Lease added to tracker!');
  };

  const deleteLease = (id) => {
    setLeases(leases.filter(lease => lease.id !== id));
    toast.success('Lease removed');
  };

  const exportToCSV = () => {
    const headers = ['Property', 'Unit', 'Tenant Name', 'Tenant Email', 'Tenant Phone', 'Lease Start', 'Lease End', 'Monthly Rent', 'Notes', 'Days Until Expiry'];
    const rows = leases.map(lease => [
      lease.property,
      lease.unit,
      lease.tenant_name,
      lease.tenant_email,
      lease.tenant_phone,
      lease.lease_start,
      lease.lease_end,
      lease.monthly_rent,
      lease.notes,
      getDaysUntilExpiry(lease.lease_end)
    ]);

    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell || ''}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lease_tracker_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Exported to CSV!');
  };

  const getDaysUntilExpiry = (endDate) => {
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryStatus = (endDate) => {
    const days = getDaysUntilExpiry(endDate);
    if (days < 0) return { label: 'Expired', color: 'bg-red-100 text-red-700', icon: AlertTriangle };
    if (days <= 30) return { label: `${days} days`, color: 'bg-red-100 text-red-700', icon: AlertTriangle };
    if (days <= 60) return { label: `${days} days`, color: 'bg-orange-100 text-orange-700', icon: Clock };
    if (days <= 90) return { label: `${days} days`, color: 'bg-yellow-100 text-yellow-700', icon: Clock };
    return { label: `${days} days`, color: 'bg-green-100 text-green-700', icon: CheckCircle };
  };

  // Sort leases by expiry date (soonest first)
  const sortedLeases = [...leases].sort((a, b) => 
    new Date(a.lease_end) - new Date(b.lease_end)
  );

  const stats = {
    total: leases.length,
    expiring30: leases.filter(l => {
      const days = getDaysUntilExpiry(l.lease_end);
      return days >= 0 && days <= 30;
    }).length,
    expiring60: leases.filter(l => {
      const days = getDaysUntilExpiry(l.lease_end);
      return days > 30 && days <= 60;
    }).length,
    expiring90: leases.filter(l => {
      const days = getDaysUntilExpiry(l.lease_end);
      return days > 60 && days <= 90;
    }).length,
    totalRent: leases.reduce((sum, l) => sum + (parseFloat(l.monthly_rent) || 0), 0)
  };

  // Email capture screen
  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Home className="h-8 w-8 text-emerald-600" />
              <span className="text-xl font-bold text-gray-900">Estate Flow</span>
            </div>
            <Badge className="bg-blue-100 text-blue-700">100% Free Tool</Badge>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="text-center mb-8">
            <div className="inline-flex p-4 bg-blue-100 rounded-full mb-4">
              <Calendar className="h-12 w-12 text-blue-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Free Lease Expiry Tracker
            </h1>
            <p className="text-xl text-gray-600 mb-2">
              Never miss a lease renewal again
            </p>
            <p className="text-gray-500">
              No credit card required • Email reminders • Co-brand with your logo
            </p>
          </div>

          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle>Get Started Free</CardTitle>
              <CardDescription>
                Enter your email to access your free lease tracker
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                  Access Free Tool
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <p className="text-xs text-center text-gray-500">
                  By continuing, you agree to receive occasional updates from Estate Flow.
                  Unsubscribe anytime.
                </p>
              </form>
            </CardContent>
          </Card>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-4 mt-8">
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <Bell className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <h3 className="font-semibold">Email Reminders</h3>
              <p className="text-sm text-gray-500">90, 60, 30 days before expiry</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <Download className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <h3 className="font-semibold">Export Anytime</h3>
              <p className="text-sm text-gray-500">Download as CSV for your records</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <Image className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <h3 className="font-semibold">Co-Brand</h3>
              <p className="text-sm text-gray-500">Add your company logo</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main tool interface
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with co-branding */}
      <header className="bg-white shadow-sm print:shadow-none">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Client logo */}
              {clientLogo ? (
                <div className="relative group">
                  <img src={clientLogo} alt="Company Logo" className="h-10 max-w-[120px] object-contain" />
                  <button
                    onClick={() => {
                      setClientLogo(null);
                      localStorage.removeItem('lease_tracker_logo');
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer flex items-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition-colors">
                  <Upload className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Add Your Logo</span>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                </label>
              )}
              
              <div className="h-8 w-px bg-gray-200" />
              
              {/* Estate Flow branding */}
              <div className="flex items-center gap-2">
                <Home className="h-6 w-6 text-emerald-600" />
                <span className="font-semibold text-gray-700">Estate Flow</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Input
                placeholder="Company Name"
                value={companyName}
                onChange={handleCompanyNameChange}
                className="w-48"
              />
              <Button variant="outline" onClick={exportToCSV} disabled={leases.length === 0}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {companyName ? `${companyName} - ` : ''}Lease Expiry Tracker
            </h1>
            <p className="text-gray-500">Track all lease renewals and never miss a deadline</p>
          </div>
          <Button onClick={() => setShowAddForm(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Lease
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-gray-500">Total Leases</div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-red-600">{stats.expiring30}</div>
              <div className="text-sm text-red-600">Expiring in 30 days</div>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-orange-600">{stats.expiring60}</div>
              <div className="text-sm text-orange-600">31-60 days</div>
            </CardContent>
          </Card>
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">{stats.expiring90}</div>
              <div className="text-sm text-yellow-600">61-90 days</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">${stats.totalRent.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Monthly Rent</div>
            </CardContent>
          </Card>
        </div>

        {/* Email Reminders Toggle */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">Email Reminders</p>
                  <p className="text-sm text-blue-700">Get notified 90, 60, and 30 days before lease expiry</p>
                </div>
              </div>
              <Switch
                checked={emailReminders}
                onCheckedChange={setEmailReminders}
              />
            </div>
          </CardContent>
        </Card>

        {/* Add Form Modal */}
        {showAddForm && (
          <Card className="mb-8 border-2 border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Add New Lease
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label>Property Address *</Label>
                  <Input
                    placeholder="123 Main Street"
                    value={newLease.property}
                    onChange={(e) => setNewLease({ ...newLease, property: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Unit/Apt</Label>
                  <Input
                    placeholder="Unit 4B"
                    value={newLease.unit}
                    onChange={(e) => setNewLease({ ...newLease, unit: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Monthly Rent</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      type="number"
                      placeholder="1500"
                      value={newLease.monthly_rent}
                      onChange={(e) => setNewLease({ ...newLease, monthly_rent: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label>Tenant Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="John Smith"
                      value={newLease.tenant_name}
                      onChange={(e) => setNewLease({ ...newLease, tenant_name: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label>Tenant Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="john@email.com"
                      value={newLease.tenant_email}
                      onChange={(e) => setNewLease({ ...newLease, tenant_email: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label>Tenant Phone</Label>
                  <Input
                    placeholder="(555) 123-4567"
                    value={newLease.tenant_phone}
                    onChange={(e) => setNewLease({ ...newLease, tenant_phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Lease Start Date</Label>
                  <Input
                    type="date"
                    value={newLease.lease_start}
                    onChange={(e) => setNewLease({ ...newLease, lease_start: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Lease End Date *</Label>
                  <Input
                    type="date"
                    value={newLease.lease_end}
                    onChange={(e) => setNewLease({ ...newLease, lease_end: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input
                    placeholder="Any special notes..."
                    value={newLease.notes}
                    onChange={(e) => setNewLease({ ...newLease, notes: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button onClick={addLease} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Lease
                </Button>
                <Button variant="outline" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Leases List */}
        {sortedLeases.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Leases Tracked Yet</h3>
              <p className="text-gray-500 mb-4">Start tracking your lease renewals by adding your first lease.</p>
              <Button onClick={() => setShowAddForm(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />
                Add First Lease
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {sortedLeases.map((lease) => {
              const status = getExpiryStatus(lease.lease_end);
              const StatusIcon = status.icon;
              return (
                <Card key={lease.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Building2 className="h-5 w-5 text-gray-400" />
                          <h3 className="font-semibold">{lease.property} {lease.unit && `- ${lease.unit}`}</h3>
                          <Badge className={status.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {status.label}
                          </Badge>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Tenant</p>
                            <p className="font-medium">{lease.tenant_name}</p>
                            {lease.tenant_email && (
                              <p className="text-gray-500 text-xs">{lease.tenant_email}</p>
                            )}
                          </div>
                          <div>
                            <p className="text-gray-500">Lease Period</p>
                            <p className="font-medium">
                              {lease.lease_start || 'N/A'} → {lease.lease_end}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Monthly Rent</p>
                            <p className="font-medium">
                              {lease.monthly_rent ? `$${parseFloat(lease.monthly_rent).toLocaleString()}` : 'N/A'}
                            </p>
                          </div>
                          {lease.notes && (
                            <div>
                              <p className="text-gray-500">Notes</p>
                              <p className="font-medium text-xs">{lease.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => deleteLease(lease.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Upgrade CTA */}
        <Card className="mt-12 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <CardContent className="py-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold mb-2">Ready for More?</h3>
                <p className="text-blue-100 mb-4">
                  Upgrade to Estate Flow for automated renewal notices, online lease signing, 
                  rent collection, and tenant screening - all in one platform.
                </p>
                <ul className="text-sm text-blue-100 space-y-1">
                  <li>✓ Automated renewal reminders to tenants</li>
                  <li>✓ Online lease signing (DocuSign integration)</li>
                  <li>✓ Rent collection & late fee automation</li>
                  <li>✓ Tenant screening & background checks</li>
                </ul>
              </div>
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-blue-50"
                onClick={() => window.location.href = '/onboarding'}
              >
                Upgrade to Estate Flow
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <footer className="mt-12 text-center text-sm text-gray-500">
          <p>Free Lease Expiry Tracker by <a href="/" className="text-blue-600 hover:underline">Estate Flow</a></p>
          <p className="mt-1">© 2025 Sebastian Alliance Group LLC. All rights reserved.</p>
        </footer>
      </main>
    </div>
  );
};

export default FreeLeaseTracker;
